import {
  Button,
  Collapse,
  Input,
  Modal,
  Popconfirm,
  Tag,
  Typography
} from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  DKIM_STATUS,
  MAIL_FROM_STATUS,
  Sender
} from '../../../../interfaces/api/Senders';
import { readWebsiteVariable } from '../../../../utils/readWebsiteVariable';
const { Text } = Typography;

const BorderedTable = styled.table`
  border: 1px #aaa solid;
  th,
  td {
    border: 1px #eee solid;
    padding: 10px;
  }
`;

type onRemoveSender = (domain: string) => Promise<void>;

export interface DomainToken {
  domain: string;
  tokens: string[];
}

type DomainDetailsProps = {
  domain: Sender;
  onRemoveSender: onRemoveSender;
};

const DomainDetails = ({
  domain,
  onRemoveSender
}: DomainDetailsProps): JSX.Element => (
  <>
    <Typography.Paragraph>
      Add the following records on your domain's DNS settings to complete the
      verification process. The verification process usually takes minutes.
    </Typography.Paragraph>
    <div style={{ overflowX: 'auto' }}>
      <BorderedTable>
        <thead>
          <tr>
            <th>Record Type</th>
            <th>Name</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {domain.verificationTokens?.map((token) => {
            return (
              <tr key={token}>
                <td>CNAME</td>
                <td>
                  <Text code copyable>
                    {`${token}._domainkey`}
                  </Text>
                </td>
                <td>
                  <Text code copyable>
                    {`${token}.dkim.amazonses.com`}
                  </Text>
                </td>
              </tr>
            );
          })}
          <tr>
            <td>MX</td>
            <td>
              <Text code copyable>
                notificationapi
              </Text>
            </td>
            <td>
              <Text code copyable>
                {`10 feedback-smtp.${readWebsiteVariable(
                  'REACT_APP_aws_project_region'
                )}.amazonses.com`}
              </Text>
            </td>
          </tr>
          <tr>
            <td>TXT</td>
            <td>
              <Text code copyable>
                notificationapi
              </Text>
            </td>
            <td>
              <div>If you don't have an SPF record:</div>
              <div>
                <Text code copyable>
                  "v=spf1 include:amazonses.com ~all"
                </Text>
              </div>
              <div style={{ marginTop: '5px' }}>
                If you already have an SPF record, insert the underlined part:
              </div>
              <div>
                <Text code>
                  "v=spf1 include:example.com{' '}
                  <Text underline>include:amazonses.com</Text> ~all"
                </Text>
              </div>
            </td>
          </tr>
          <tr>
            <td>TXT</td>
            <td>
              <Text code copyable>
                _dmarc
              </Text>
            </td>
            <td>
              <div>
                <Text code copyable>
                  "v=DMARC1; p=none;"
                </Text>
              </div>
            </td>
          </tr>
        </tbody>
      </BorderedTable>
    </div>
  </>
);

type DomainsPanelProps = {
  modalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
  onRemoveSender: onRemoveSender;
  onSubmitDomain: (domain: string) => void;
  allDomains: Sender[];
  submittingDomain: boolean;
};

const DomainsPanel = ({
  modalVisible,
  setModalVisible,
  onRemoveSender,
  onSubmitDomain,
  allDomains,
  submittingDomain
}: DomainsPanelProps): JSX.Element => {
  const [newDomainInputValue, setNewDomainInputValue] = useState('');
  return (
    <>
      <Paragraph type="secondary" style={{ maxWidth: 810 }}>
        Verifying a domain allows your emails to be sent from any address under
        your domain. For example, by verifying example.com, you can send emails
        from noreply@example.com or any subdomains under example.com.
      </Paragraph>

      {/* CloudValid */}
      {allDomains
        .filter((domain) => domain.createdAt >= '2024-04-10T00:00:00')
        .map((domain) => {
          return (
            <Collapse
              key={domain.sender}
              defaultActiveKey={'0'}
              style={{ marginBottom: 20 }}
            >
              <Collapse.Panel
                key="0"
                header={
                  <>
                    <Text strong style={{ marginRight: 10 }}>
                      {domain.sender}
                    </Text>
                  </>
                }
              >
                <iframe
                  title="Domain Verification"
                  src={`https://app.cloudvalid.com/config/aed44fd9-6261-4b96-a618-af26fb917002:${domain.sender}`}
                  style={{ width: '100%', height: 950, border: 'none' }}
                ></iframe>

                <Popconfirm
                  title="Are you sure?"
                  description="Emails sent using this address will go out from notificationapi.com's domain."
                  onConfirm={async () => {
                    await onRemoveSender(domain.sender);
                  }}
                  okType="danger"
                  okText="Yes, delete it!"
                >
                  <Button type="primary" danger>
                    Delete this domain
                  </Button>
                </Popconfirm>
              </Collapse.Panel>
            </Collapse>
          );
        })}

      {allDomains
        .filter((domain) => domain.createdAt < '2024-04-10T00:00:00')
        .map((domain) => {
          return (
            <Collapse
              key={domain.sender}
              defaultActiveKey={
                domain.status === DKIM_STATUS.SUCCESS &&
                domain.mailFromDomainStatus === MAIL_FROM_STATUS.SUCCESS
                  ? ''
                  : '0'
              }
              style={{ marginBottom: 20 }}
            >
              <Collapse.Panel
                key="0"
                header={
                  <>
                    <Text strong style={{ marginRight: 10 }}>
                      {domain.sender}
                    </Text>

                    {domain.status.toString() ===
                      domain.mailFromDomainStatus?.toString() && (
                      <Tag
                        color={
                          domain.status === DKIM_STATUS.SUCCESS
                            ? 'green'
                            : 'warning'
                        }
                      >
                        {domain.status}
                      </Tag>
                    )}
                    {domain.status.toString() !==
                      domain.mailFromDomainStatus?.toString() && (
                      <>
                        <Tag
                          color={
                            domain.status === DKIM_STATUS.SUCCESS
                              ? 'green'
                              : 'warning'
                          }
                        >
                          DKIM: {domain.status}
                        </Tag>
                        <Tag
                          color={
                            domain.mailFromDomainStatus ===
                            MAIL_FROM_STATUS.SUCCESS
                              ? 'green'
                              : 'warning'
                          }
                        >
                          SPF:{' '}
                          {domain.mailFromDomainStatus ??
                            'Not Verified - Contact Support'}
                        </Tag>
                      </>
                    )}
                  </>
                }
              >
                <DomainDetails
                  key={domain.sender}
                  domain={domain}
                  onRemoveSender={onRemoveSender}
                />
                <Popconfirm
                  title="Are you sure?"
                  description="Emails sent using this address will go out from notificationapi.com's domain."
                  onConfirm={async () => {
                    await onRemoveSender(domain.sender);
                  }}
                  okType="danger"
                  okText="Yes, delete it!"
                >
                  <Button type="primary" danger style={{ marginTop: 20 }}>
                    Delete this domain
                  </Button>
                </Popconfirm>
              </Collapse.Panel>
            </Collapse>
          );
        })}

      <Button
        onClick={(): void => {
          setModalVisible(true);
        }}
        type={allDomains.length > 0 ? 'default' : 'primary'}
      >
        {allDomains.length > 0 ? 'Verify another domain' : 'Verify your domain'}
      </Button>

      <Modal
        title="Domain Verification"
        style={{ top: '20vh' }}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={
          <>
            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => {
                onSubmitDomain(newDomainInputValue);
              }}
              loading={submittingDomain}
            >
              Submit
            </Button>
          </>
        }
      >
        Enter your domain. Do not include https, www or subdomain.
        <Input
          data-testid="dkim-verify-input"
          placeholder="yourwebsite.com"
          defaultValue={newDomainInputValue}
          onChange={({ target: { value } }): void =>
            setNewDomainInputValue(value)
          }
          style={{ marginTop: '10px' }}
        />
      </Modal>
    </>
  );
};

export default DomainsPanel;
